import {
  Alert,
  AlertProps,
  AlertTitle,
  Button,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { OxCategoriesNames } from '@oxappsec/ox-consolidated-categories';
import { FC, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { AppPages } from '../../common/routing/app-navigator';
import {
  setModalRemoveSnooze,
  setModalUpdateExpiredAt,
  setModalUpdateExpiredAtBanner,
  setSelectedExclusion,
} from '../store-actions/exclusions-store-actions';
import { Exclusion } from '../types/exclusion-types';

const SnoozeExpiredBanner: FC<SnoozeExpiredBannerProps> = ({
  week,
  day,
  exclusionsExpiredWeek,
  ...restOfProps
}) => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  const handleUpdateExpireDate = useCallback(
    (exclusion: Exclusion) => {
      navigate(AppPages.Exclusions);
      setSelectedExclusion(exclusion);
      setModalUpdateExpiredAt(true);
      setModalUpdateExpiredAtBanner(true);
    },
    [navigate],
  );

  const handleRemoveExpireDate = useCallback(
    (exclusion: Exclusion) => {
      navigate(AppPages.Exclusions);
      setSelectedExclusion(exclusion);
      setModalRemoveSnooze(true);
    },
    [navigate],
  );

  return (
    <Alert
      severity='warning'
      {...restOfProps}
      onClose={week || day ? restOfProps.onClose : undefined}>
      {week ? (
        <AlertTitle>
          The following exclusions are set to expire in a week:
        </AlertTitle>
      ) : (
        <AlertTitle>
          The following exclusions are set to expire in a day:
        </AlertTitle>
      )}
      <div className={classes.expiredExclusions}>
        {exclusionsExpiredWeek.slice(0, 3).map(exclusion => {
          return (
            <Typography
              variant='body2'
              color='text.primary'
              key={exclusion.id}
              mb={0.5}
              className={classes.exclusion}>
              {`${exclusion.exclusionAppliedOn} - ${exclusion.appName}`}
              <Button
                size='small'
                className={classes.buttonExclusion}
                onClick={() => handleUpdateExpireDate(exclusion)}>
                Update
              </Button>
              <Button
                size='small'
                className={classes.buttonExclusion}
                onClick={() => handleRemoveExpireDate(exclusion)}>
                Make Exclusion Permanent
              </Button>
            </Typography>
          );
        })}
        {exclusionsExpiredWeek.length > 3 && (
          <Tooltip
            title={
              <div>
                {exclusionsExpiredWeek
                  .slice(3, exclusionsExpiredWeek.length)
                  .map(exclusion => {
                    return (
                      <Typography
                        variant='body2'
                        color='text.primary'
                        key={exclusion.id}
                        mb={0.5}>
                        {`${exclusion.exclusionAppliedOn} - ${exclusion.appName}`}
                      </Typography>
                    );
                  })}
              </div>
            }>
            <NavLink
              to={`${AppPages.Exclusions}?search=${OxCategoriesNames.SourceControl}`}>
              {`+ ${exclusionsExpiredWeek.length - 3} More`}
            </NavLink>
          </Tooltip>
        )}
      </div>
    </Alert>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  expiredExclusions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  exclusion: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonExclusion: {
    backgroundColor: 'transparent',
    fontSize: '0.65rem',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

interface SnoozeExpiredBannerProps extends AlertProps {
  week: boolean;
  day: boolean;
  exclusionsExpiredWeek: Exclusion[];
}
export default SnoozeExpiredBanner;
